import React, { useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useDomainManager } from '../../config';
import { renderCtaButton } from '../../utils/ctaHelper';
import { handleImageErrorWithFallback } from '../../utils/imageManager';
import { usePageTitle } from '../../hooks/usePageTitle';

const ModernVariant = ({ heroContent, brand }) => {
  const mainImageRef = useRef(null);
  const secondaryImageRef = useRef(null);
  const { domainConfig, cta } = useTheme();
  const domainManager = useDomainManager(brand?.id);
  
  // Get hostname-specific H1 title
  const h1Title = usePageTitle('home');

  // Reset image elements when domain changes
  useEffect(() => {
    if (mainImageRef.current) {
      Object.keys(mainImageRef.current.dataset).forEach(key => {
        if (key.startsWith('triedExtensions_')) {
          delete mainImageRef.current.dataset[key];
        }
      });
    }
    if (secondaryImageRef.current) {
      Object.keys(secondaryImageRef.current.dataset).forEach(key => {
        if (key.startsWith('triedExtensions_')) {
          delete secondaryImageRef.current.dataset[key];
        }
      });
    }
  }, [brand.id]);

  // Get domain-specific features content using domain manager
  const getDomainFeatures = () => {
    const features = domainManager.getFeatures();
    if (features && features.items && features.items.length > 0) {
      return {
        title: features.title,
        subtitle: features.subtitle,
        description: features.description,
        features: features.items
      };
    }
    
    // Fallback to hero content features if no domain features configured
    return {
      title: 'Advanced Technology',
      subtitle: 'Cutting-edge solutions powered by innovation',
      description: 'Next-generation technology designed to revolutionize your business operations with intelligent automation.',
      features: heroContent.features || []
    };
  };

  // Get domain-specific testimonials content using domain manager
  const getDomainTestimonials = () => {
    const testimonials = domainManager.getTestimonials();
    if (testimonials && testimonials.length > 0) {
      return testimonials;
    }
    return null;
  };

  // Get domain-specific solutions content using domain manager
  const getDomainSolutions = () => {
    const solutions = domainManager.getSolutions();
    if (solutions && solutions.sections && solutions.sections.length > 0) {
      return {
        title: solutions.title,
        subtitle: solutions.subtitle,
        description: solutions.description,
        sections: solutions.sections
      };
    }
    return null;
  };

  const { mainImage, secondaryImage, title, subtitle, features } = heroContent;
  const featuresData = getDomainFeatures();
  const testimonialsData = getDomainTestimonials();
  const solutionsData = getDomainSolutions();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Hero Section - Modern Tech Design */}
      <section className="relative pt-20 pb-32 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse delay-1000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse delay-2000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          {/* Brand Badge */}
          <div className="mb-8">
            <span className="inline-block px-6 py-3 text-sm font-semibold text-white bg-white/10 backdrop-blur-md rounded-full border border-white/20">
              {brand.name}
            </span>
          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Content */}
            <div className="space-y-8">
              <div>
                <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight mb-6 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
                  {h1Title || title}
                </h1>
                <p className="text-xl text-purple-100 leading-relaxed">
                  {subtitle}
                </p>
              </div>

              {/* Features - Modern Grid */}
              <div className="grid grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3 p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                    <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full"></div>
                    <span className="text-white font-medium">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons - Modern Style */}
              <div className="flex space-x-4 pt-8">
                {renderCtaButton(cta?.primary, 'Request Demo', 'px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1', domainConfig?.shopLink)}
                {renderCtaButton(cta?.secondary, 'Buy Now', 'px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-xl hover:bg-white/10 transition-all duration-300 backdrop-blur-sm', domainConfig?.shopLink)}
              </div>
            </div>

            {/* Right Column - Images */}
            <div className="relative">
              {/* Main Image with Glow Effect */}
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
                <div className="relative">
                  <img 
                    ref={mainImageRef}
                    src={mainImage} 
                    alt={title}
                    className="w-full h-64 md:h-96 object-cover rounded-2xl"
                    onError={(e) => handleImageErrorWithFallback(e, brand.id, 'hero-main')}
                  />
                </div>
              </div>
              
              {/* Secondary Image - Floating with Animation */}
              <div className="absolute -bottom-8 -left-8 w-48 h-32 animate-float">
                <div className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl blur opacity-50 group-hover:opacity-75 transition duration-1000"></div>
                  <div className="relative">
                    <img 
                      ref={secondaryImageRef}
                      src={secondaryImage} 
                      alt={`${title} - Secondary`}
                      className="w-full h-full object-cover rounded-xl shadow-2xl"
                      onError={(e) => handleImageErrorWithFallback(e, brand.id, 'hero-secondary')}
                    />
                  </div>
                </div>
              </div>

              {/* Tech Elements */}
              <div className="absolute top-4 right-4 w-16 h-16 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center border border-white/20">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full flex items-center justify-center">
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Modern Cards */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '1000+', label: 'Satisfied Customers', color: 'from-purple-500 to-pink-500' },
              { number: '11+', label: 'Years of Experience', color: 'from-blue-500 to-purple-500' },
              { number: '24/7', label: 'Support', color: 'from-green-500 to-blue-500' },
              { number: '5★', label: 'Rating', color: 'from-yellow-500 to-orange-500' }
            ].map((stat, index) => (
              <div key={index} className="text-center p-4 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-300">
                <div className={`text-4xl font-bold mb-2 bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                  {stat.number}
                </div>
                <div className="text-purple-200 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Modern Tech Cards */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">{featuresData.title}</h2>
            <p className="text-purple-200 text-xl">{featuresData.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {featuresData.features.map((feature, index) => (
              <div key={index} className="group p-8 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl mx-auto mb-6 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                    <div className="w-4 h-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded"></div>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-4 text-center">
                  {typeof feature === 'string' ? feature : feature.title}
                </h3>
                <p className="text-purple-200 text-center leading-relaxed">
                  {typeof feature === 'string' ? featuresData.description : feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section - Modern Cards */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">What Our Customers Say</h2>
            <p className="text-purple-200 text-xl">Trusted by businesses worldwide</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonialsData.map((testimonial, index) => (
              <div key={index} className="group p-8 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">
                        {testimonial.author?.charAt(0) || 'C'}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <blockquote className="text-purple-100 text-lg leading-relaxed mb-4">
                      "{testimonial.content}"
                    </blockquote>
                    <div className="flex items-center space-x-2">
                      <div>
                        <div className="text-white font-semibold">{testimonial.author}</div>
                        <div className="text-purple-300 text-sm">{testimonial.role}</div>
                        <div className="text-purple-400 text-sm">{testimonial.company}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default ModernVariant;
